import { forwardRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { translate } from '../../../helpers/i18n';
import { CommonFormField, CommonFormFieldProps } from '../../../components';
import { SingleSelectDropdown } from '../../../components/Dropdowns/SingleSelectDropdown';
import { useGetRfiTypes } from '../../../hooks/queries/rfiTypesQueries';
import Wfa from './assets/wfa.svg';
import Wfb from './assets/wfb.svg';
import { EntityStatus, RfiType, WorkflowType } from '../../../sdk';
import { FormFieldInfoPopover } from '../../../components/Form/FormFieldInfoPopover';

const Diagrams = styled.div`
  margin-top: 10px;
`;

const renderInfoPopover: CommonFormFieldProps['renderInfoPopover'] = (props) => (
  <FormFieldInfoPopover {...props} message={translate('tooltips.rfiType')} />
);

type PureRfiTypeFieldProps = {
  required?: boolean;
  errorMessage?: string | boolean;
  selectedRfiTypeId: string;
  setRfiType: (id: string) => void;
  showDiagrams?: boolean;
};

export const PureRfiTypeField = forwardRef<HTMLDivElement, PureRfiTypeFieldProps>(
  ({ required = false, errorMessage, selectedRfiTypeId, setRfiType, showDiagrams = true }, ref) => {
    const { data: rfiTypes } = useGetRfiTypes();
    const activeTypes = useMemo(
      () =>
        rfiTypes?.filter(
          ({ status, id }) => status === EntityStatus.Active || id === selectedRfiTypeId,
        ),
      [rfiTypes],
    );
    // We expect from BE for at least 1 rfiType, if we get [] it means migration hasn't completed yet.
    // and we should consider it as not fetched yet
    const isRfiTypesFetched = Array.isArray(activeTypes) && activeTypes.length > 0;

    const dropdownOptions = useMemo(
      () =>
        activeTypes?.map(({ id, name, wfType, status }) => ({
          value: id,
          label: name,
          meta: { wfType },
          disabled: status === EntityStatus.Inactive,
        })),
      [activeTypes],
    );

    // Populate default rfi type on form load
    useEffect(() => {
      if (isRfiTypesFetched && !selectedRfiTypeId) {
        const defaultRfiId = (activeTypes as RfiType[])[0].id;
        setRfiType(defaultRfiId);
      }
    }, [isRfiTypesFetched]);

    const selectedRfiType = activeTypes?.find((rfiType) => rfiType.id === selectedRfiTypeId);

    return (
      <CommonFormField
        label={translate('new.rfiType')}
        loading={!isRfiTypesFetched}
        required={required}
        error={errorMessage}
        ref={ref}
        renderInfoPopover={renderInfoPopover}
      >
        <SingleSelectDropdown
          options={dropdownOptions || []}
          value={selectedRfiTypeId}
          onChange={setRfiType}
        />
        {showDiagrams && (
          <Diagrams>
            {selectedRfiType?.wfType === WorkflowType.US && <Wfa />}
            {selectedRfiType?.wfType === WorkflowType.EU && <Wfb />}
          </Diagrams>
        )}
      </CommonFormField>
    );
  },
);
